import { supabase } from '@/integrations/supabase/client';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { type Company } from '@/types/company';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a full name from first and last names
 * @param firstName - The first name
 * @param lastName - The last name
 * @returns The formatted full name
 */
export function formatFullName(firstName: string | null, lastName: string | null): string {
  const first = firstName?.trim() || '';
  const last = lastName?.trim() || '';

  if (first && last) {
    return `${first} ${last}`;
  } else if (first) {
    return first;
  } else if (last) {
    return last;
  } else {
    return 'Unknown';
  }
}

/**
 * Smart title case formatting that handles small words appropriately
 * @param text - The text to format
 * @returns The formatted text with proper capitalization
 */
export function toSmartTitleCase(text: any): string {
  if (!text || typeof text !== 'string') return '';

  // Words that should stay lowercase unless they're the first or last word
  const smallWords = new Set([
    'a',
    'an',
    'and',
    'as',
    'at',
    'but',
    'by',
    'for',
    'if',
    'in',
    'nor',
    'of',
    'on',
    'or',
    'so',
    'the',
    'to',
    'up',
    'yet',
    'vs',
    'via',
    'per',
    'with',
  ]);

  // Split by spaces and process each word
  const words = text.toLowerCase().split(/\s+/);

  return words
    .map((word, index) => {
      if (!word) return word;

      // Always capitalize first and last word
      if (index === 0 || index === words.length - 1) {
        return capitalizeWord(word);
      }

      // Keep small words lowercase unless they start the sentence
      if (smallWords.has(word)) {
        return word;
      }

      return capitalizeWord(word);
    })
    .join(' ');
}

/**
 * Capitalizes a single word, handling special cases
 * @param word - The word to capitalize
 * @returns The capitalized word
 */
function capitalizeWord(word: any): string {
  if (!word || typeof word !== 'string') return '';

  // Handle hyphenated words (e.g., "co-founder" -> "Co-Founder")
  if (word.includes('-')) {
    return word
      .split('-')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join('-');
  }

  // Handle words with apostrophes (e.g., "mcdonald's" -> "McDonald's")
  if (word.includes("'")) {
    const parts = word.split("'");
    return parts
      .map((part, idx) => {
        if (idx === 0) return part.charAt(0).toUpperCase() + part.slice(1);
        return part; // Keep part after apostrophe as is for possessives
      })
      .join("'");
  }

  // Handle "mc" and "mac" prefixes (e.g., "mcdonald" -> "McDonald")
  if (word.startsWith('mc') && word.length > 2) {
    return 'Mc' + word.charAt(2).toUpperCase() + word.slice(3);
  }

  if (word.startsWith('mac') && word.length > 3) {
    return 'Mac' + word.charAt(3).toUpperCase() + word.slice(4);
  }

  // Standard capitalization
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Formats company names with intelligent capitalization
 * @param company - The company name to format
 * @returns The formatted company name
 */
export function formatCompanyName(company: any): string {
  if (!company || typeof company !== 'string') return '';

  // Handle common company abbreviations and special cases
  const specialCases = {
    llc: 'LLC',
    inc: 'Inc.',
    corp: 'Corp.',
    ltd: 'Ltd.',
    co: 'Co.',
    llp: 'LLP',
    lp: 'LP',
    usa: 'USA',
    us: 'US',
    uk: 'UK',
    it: 'IT',
    ai: 'AI',
    api: 'API',
    ceo: 'CEO',
    cto: 'CTO',
    cfo: 'CFO',
    hr: 'HR',
    pr: 'PR',
    'r&d': 'R&D',
  };

  let formatted = toSmartTitleCase(company);

  // Apply special cases
  Object.entries(specialCases).forEach(([key, value]) => {
    const regex = new RegExp(`\\b${key}\\b`, 'gi');
    formatted = formatted.replace(regex, value);
  });

  return formatted;
}

/**
 * Formats job titles with intelligent capitalization
 * @param title - The job title to format
 * @returns The formatted job title
 */
export function formatJobTitle(title: any): string {
  if (!title || typeof title !== 'string') return '';

  // Handle common job title abbreviations
  const specialCases = {
    ceo: 'CEO',
    cto: 'CTO',
    cfo: 'CFO',
    coo: 'COO',
    vp: 'VP',
    svp: 'SVP',
    evp: 'EVP',
    hr: 'HR',
    it: 'IT',
    ai: 'AI',
    ml: 'ML',
    ui: 'UI',
    ux: 'UX',
    qa: 'QA',
    devops: 'DevOps',
    sre: 'SRE',
    api: 'API',
    sdk: 'SDK',
    crm: 'CRM',
    erp: 'ERP',
    seo: 'SEO',
    sem: 'SEM',
    ppc: 'PPC',
    b2b: 'B2B',
    b2c: 'B2C',
    saas: 'SaaS',
    paas: 'PaaS',
    iaas: 'IaaS',
  };

  let formatted = toSmartTitleCase(title);

  // Apply special cases
  Object.entries(specialCases).forEach(([key, value]) => {
    const regex = new RegExp(`\\b${key}\\b`, 'gi');
    formatted = formatted.replace(regex, value);
  });

  return formatted;
}

/**
 * Formats experience level strings with proper capitalization and spacing
 * @param level - The experience level string (e.g., "senior-level", "mid-level")
 * @returns The formatted experience level (e.g., "Senior Level", "Mid Level")
 */
export function formatExperienceLevel(level: string): string {
  if (!level || typeof level !== 'string') return '';

  switch (level) {
    case 'entry-level':
      return 'Entry Level';
    case 'mid-level':
      return 'Mid Level';
    case 'senior-level':
      return 'Senior Level';
    case 'executive-level':
      return 'Executive Level';
    default:
      return level
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
  }
}

/**
 * Transform a resume URL for local development
 * In development, this creates a fresh signed URL and replaces local URLs with tunnel URLs
 * @param resumeUrl - The original resume URL from the database
 * @returns Promise<string | null> - The transformed URL or null if transformation fails
 */
export async function getResumeUrl(resumeUrl: string | null | undefined): Promise<string | null> {
  if (!resumeUrl) return null;

  // In production, handle simple cases
  if (import.meta.env.MODE !== 'development') {
    // If it's already a full URL, return as-is
    if (resumeUrl.startsWith('http')) {
      return resumeUrl;
    }
    // For raw storage paths, generate proper Supabase storage URL
    try {
      const { data } = supabase.storage.from('resumes').getPublicUrl(resumeUrl);
      return data.publicUrl;
    } catch (error) {
      console.error('Error generating public URL:', error);
      return null;
    }
  }

  // Development: Need to handle localhost URLs with tunnel transformation
  try {
    let filePath: string;

    // Extract file path from URL
    if (resumeUrl.includes('/resumes/')) {
      // URL format: http://127.0.0.1:54321/storage/v1/object/public/resumes/[path]
      const urlParts = resumeUrl.split('/resumes/');
      if (urlParts.length < 2) {
        console.warn('Unable to extract file path from resume URL:', resumeUrl);
        return resumeUrl;
      }
      filePath = urlParts[1].split('?')[0]; // Remove query parameters
    } else {
      // Assume it's already a file path
      filePath = resumeUrl.split('?')[0];
    }

    // Check if we have tunnel URL configured
    const TUNNEL_MOLE_URL = import.meta.env.VITE_TUNNEL_MOLE_URL;
    if (!TUNNEL_MOLE_URL) {
      console.warn('TUNNEL_MOLE_URL not configured for local development');
      return resumeUrl; // Return original as fallback
    }

    // Create a signed URL for external access
    const { data, error } = await supabase.storage.from('resumes').createSignedUrl(filePath, 3600); // 1 hour expiration

    if (error || !data) {
      console.error('Error creating signed URL:', error);
      return resumeUrl;
    }

    // Replace localhost with tunnel URL
    const LOCAL_URL = import.meta.env.VITE_SUPABASE_URL;
    const tunnelUrl = data.signedUrl.replace(LOCAL_URL, TUNNEL_MOLE_URL);

    console.log('Transformed resume URL for local development:', {
      original: resumeUrl,
      signed: data.signedUrl,
      tunnel: tunnelUrl,
    });

    return tunnelUrl;
  } catch (error) {
    console.error('Error transforming resume URL:', error);
    return resumeUrl;
  }
}

/**
 * Calculate years of experience from candidate data using robust parsing logic
 * This matches the logic used in prepare-interview-ai function
 */
export function calculateYearsOfExperience(experience: any): number {
  if (!experience) return 0;

  let yearsOfExperience = 0;

  // PREFERRED: Calculate from positions_held for accuracy (if available)
  if (experience.positions_held && experience.positions_held.length > 0) {
    let totalYears = 0;

    for (const position of experience.positions_held) {
      if (position.dates) {
        // Parse various date formats: "2010 - 2014", "01/2010 - 12/2014", "2022 - Present"
        const dateRangeMatch = position.dates.match(/(\d{4})\s*-\s*(\d{4}|Present)/i);
        if (dateRangeMatch) {
          const startYear = parseInt(dateRangeMatch[1], 10);
          const endYear =
            dateRangeMatch[2].toLowerCase() === 'present'
              ? new Date().getFullYear()
              : parseInt(dateRangeMatch[2], 10);
          totalYears += Math.max(0, endYear - startYear);
        } else {
          // Try MM/YYYY format: "01/2010 - 12/2014"
          const monthYearMatch = position.dates.match(
            /(\d{1,2})\/(\d{4})\s*-\s*(\d{1,2}\/(\d{4})|Present)/i
          );
          if (monthYearMatch) {
            const startYear = parseInt(monthYearMatch[2], 10);
            const endYear =
              monthYearMatch[3] && monthYearMatch[3].toLowerCase() === 'present'
                ? new Date().getFullYear()
                : parseInt(monthYearMatch[4], 10);
            totalYears += Math.max(0, endYear - startYear);
          }
        }
      }
    }

    if (totalYears > 0) {
      yearsOfExperience = totalYears;
    }
  }

  // FALLBACK: Use explicit years field if positions calculation failed
  if (yearsOfExperience === 0 && experience.years) {
    // Convert to string if it's a number
    const yearsString =
      typeof experience.years === 'string' ? experience.years : String(experience.years);

    // Check if it's a date range (e.g., "2014 - Present", "2014 - 2020")
    const dateRangeMatch = yearsString.match(/(\d{4})\s*-\s*(\d{4}|Present)/i);
    if (dateRangeMatch) {
      const startYear = parseInt(dateRangeMatch[1], 10);
      const endYear =
        dateRangeMatch[2].toLowerCase() === 'present'
          ? new Date().getFullYear()
          : parseInt(dateRangeMatch[2], 10);
      yearsOfExperience = Math.max(1, endYear - startYear);
    } else {
      // Try to parse various formats: "14", "14+ years", "5-7 years", "10 years"
      const yearsMatch = yearsString.match(/^(\d+)(?:\+|\s*-\s*\d+)?\s*(?:years?)?$/i);
      if (yearsMatch) {
        yearsOfExperience = parseInt(yearsMatch[1], 10);
      }
    }
  }

  return yearsOfExperience;
}

/**
 * Ensures a URL has a proper protocol (https://) if it doesn't already have one
 * @param url - The URL to check and potentially modify
 * @returns The URL with proper protocol or empty string if invalid
 */
export function ensureHttpsProtocol(url: any): string {
  if (!url || typeof url !== 'string') return '';

  // If the URL already has a protocol, return it as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // If it starts with www. or a domain, prepend https://
  if (url.startsWith('www.') || url.includes('.')) {
    return `https://${url}`;
  }

  return url;
}

// Gender detection for AI candidate voice selection
const GENDER_CACHE_KEY = 'interview_gender_cache';
const CACHE_EXPIRY_DAYS = 30;

interface GenderCacheEntry {
  gender: 'male' | 'female' | 'unknown';
  timestamp: number;
}

interface GenderCache {
  [firstName: string]: GenderCacheEntry;
}

const VOICE_MAPPING = {
  female: 'alloy',
  male: 'ash',
  unknown: 'ash',
} as const;

/**
 * Get cached gender detection result from localStorage
 */
function getCachedGender(firstName: string): 'male' | 'female' | 'unknown' | null {
  try {
    const cacheStr = localStorage.getItem(GENDER_CACHE_KEY);
    if (!cacheStr) return null;

    const cache: GenderCache = JSON.parse(cacheStr);
    const normalizedName = firstName.toLowerCase().trim();
    const entry = cache[normalizedName];

    if (!entry) return null;

    // Check if entry is expired (30 days)
    const now = Date.now();
    const expiryTime = entry.timestamp + CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    if (now > expiryTime) {
      // Remove expired entry
      delete cache[normalizedName];
      localStorage.setItem(GENDER_CACHE_KEY, JSON.stringify(cache));
      return null;
    }

    return entry.gender;
  } catch (error) {
    console.warn('Error reading gender cache:', error);
    return null;
  }
}

/**
 * Store gender detection result in localStorage with timestamp
 */
function setCachedGender(firstName: string, gender: 'male' | 'female' | 'unknown'): void {
  try {
    const cacheStr = localStorage.getItem(GENDER_CACHE_KEY);
    let cache: GenderCache = {};

    if (cacheStr) {
      cache = JSON.parse(cacheStr);
    }

    const normalizedName = firstName.toLowerCase().trim();
    cache[normalizedName] = {
      gender,
      timestamp: Date.now(),
    };

    // Cleanup: limit cache size to 100 entries (keep most recent)
    const entries = Object.entries(cache);
    if (entries.length > 100) {
      // Sort by timestamp (newest first) and keep only top 100
      const sortedEntries = entries.sort(([, a], [, b]) => b.timestamp - a.timestamp).slice(0, 100);

      cache = Object.fromEntries(sortedEntries);
    }

    localStorage.setItem(GENDER_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('Error writing to gender cache:', error);
  }
}

/**
 * Detect gender using OpenAI API via our openai-chat edge function
 */
async function detectGenderFromAPI(firstName: string): Promise<'male' | 'female' | 'unknown'> {
  try {
    const { supabase } = await import('@/integrations/supabase/client');

    const { data, error } = await supabase.functions.invoke('openai-chat', {
      body: {
        messages: [
          {
            role: 'system',
            content:
              'You are a helpful assistant that determines the most likely gender/sex associated with first names. Respond with only "male", "female", or "unknown" (for unisex names, unclear cases, or non-English names where you are uncertain).',
          },
          {
            role: 'user',
            content: `What is the most likely gender/sex for the first name "${firstName}"? Consider common usage patterns. If the name is unisex, ambiguous, or you are uncertain, respond with "unknown".`,
          },
        ],
        model: 'gpt-4o-mini',
        temperature: 0.1,
        max_tokens: 10,
        context: 'gender_detection_for_voice_selection',
      },
    });

    if (error) {
      console.warn('OpenAI API error for gender detection:', error);
      return 'unknown';
    }

    if (!data?.choices?.[0]?.message?.content) {
      console.warn('Invalid response structure from OpenAI API');
      return 'unknown';
    }

    const response = data.choices[0].message.content.toLowerCase().trim();

    if (response.includes('female')) return 'female';
    if (response.includes('male')) return 'male';

    return 'unknown';
  } catch (error) {
    console.warn('Error detecting gender from API:', error);
    return 'unknown';
  }
}

/**
 * Detect gender for a first name with caching
 * Returns appropriate voice for AI candidate based on detected gender
 */
export async function detectGenderAndGetVoice(firstName: string): Promise<string> {
  if (!firstName?.trim()) {
    return VOICE_MAPPING.unknown;
  }

  const normalizedName = firstName.trim();

  // Check cache first
  const cachedGender = getCachedGender(normalizedName);
  if (cachedGender) {
    console.log(
      `🗣️ Using cached gender for "${normalizedName}": ${cachedGender} → voice: ${VOICE_MAPPING[cachedGender]}`
    );
    return VOICE_MAPPING[cachedGender];
  }

  console.log(`🗣️ Detecting gender for "${normalizedName}"...`);

  // Detect gender via API
  const detectedGender = await detectGenderFromAPI(normalizedName);

  // Cache the result
  setCachedGender(normalizedName, detectedGender);

  const selectedVoice = VOICE_MAPPING[detectedGender];
  console.log(
    `🗣️ Detected gender for "${normalizedName}": ${detectedGender} → voice: ${selectedVoice}`
  );

  return selectedVoice;
}

/**
 * Company Handle Generation Utilities
 * Following industry standards for handle/username generation
 */

/**
 * Generates a base handle from a company name following industry standards
 * @param companyName - The company name to generate a handle from
 * @returns A suggested base handle
 */
export function generateHandleFromCompanyName(companyName: string): string {
  if (!companyName || typeof companyName !== 'string') {
    return '';
  }

  // Remove common business suffixes and prefixes
  const businessTerms = [
    'inc',
    'inc.',
    'incorporated',
    'corporation',
    'corp',
    'corp.',
    'llc',
    'l.l.c.',
    'ltd',
    'ltd.',
    'limited',
    'company',
    'co',
    'co.',
    'group',
    'holdings',
    'enterprises',
    'solutions',
    'services',
    'consulting',
    'consultancy',
    'technologies',
    'technology',
    'tech',
    'systems',
    'international',
    'intl',
    'global',
    'worldwide',
    'partners',
    'partnership',
    'associates',
    'associates',
    'ventures',
    'capital',
    'investments',
    'industries',
  ];

  let cleaned = companyName.toLowerCase().trim();

  // Remove business terms (case insensitive)
  businessTerms.forEach(term => {
    const regex = new RegExp(`\\b${term}\\b`, 'gi');
    cleaned = cleaned.replace(regex, '');
  });

  // Split into words and process
  const words = cleaned
    .split(/[\s\-_&+.,()]+/)
    .filter(word => word.length > 0)
    .map(word => word.trim());

  if (words.length === 0) {
    // Fallback to original name if all words were filtered out
    return companyName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 20);
  }

  let handle = '';

  if (words.length === 1) {
    // Single word: use first 8 characters or full word if shorter
    handle = words[0].substring(0, 8);
  } else if (words.length === 2) {
    // Two words: try acronym first, then combinations
    const acronym = words.map(w => w.charAt(0)).join('');
    if (acronym.length >= 2) {
      handle = acronym;
    } else {
      // Combine first few characters of each word
      handle = words[0].substring(0, 4) + words[1].substring(0, 4);
    }
  } else if (words.length >= 3) {
    // Three or more words: prioritize acronym approach
    const acronym = words.map(w => w.charAt(0)).join('');
    if (acronym.length >= 3 && acronym.length <= 6) {
      handle = acronym;
    } else {
      // Use first letter of each of first 3 words + first word
      const firstLetters = words
        .slice(0, 3)
        .map(w => w.charAt(0))
        .join('');
      const firstWord = words[0].substring(0, 4);
      handle = firstLetters + firstWord;
    }
  }

  // Clean the handle: only alphanumeric, lowercase
  handle = handle.replace(/[^a-z0-9]/g, '').toLowerCase();

  // Ensure minimum length of 2, maximum of 20
  if (handle.length < 2) {
    // Fallback: use first word
    handle = words[0].substring(0, Math.min(8, words[0].length));
  }

  handle = handle.substring(0, 20);

  return handle;
}

/**
 * Generates multiple handle suggestions for a company name
 * @param companyName - The company name to generate handles for
 * @returns An array of suggested handles
 */
export function generateHandleSuggestions(companyName: string): string[] {
  const baseHandle = generateHandleFromCompanyName(companyName);
  const suggestions: string[] = [baseHandle];

  if (!baseHandle) {
    return suggestions;
  }

  // Split company name into words for additional suggestions
  const words = companyName
    .toLowerCase()
    .split(/[\s\-_&+.,()]+/)
    .filter(word => word.length > 0)
    .map(word => word.replace(/[^a-z0-9]/g, ''));

  // Strategy 1: First word + abbreviated second word
  if (words.length >= 2) {
    const firstWord = words[0].substring(0, 6);
    const secondAbbrev = words[1].substring(0, 2);
    suggestions.push(firstWord + secondAbbrev);
  }

  // Strategy 2: Abbreviated first + full second word
  if (words.length >= 2) {
    const firstAbbrev = words[0].substring(0, 3);
    const secondWord = words[1].substring(0, 5);
    suggestions.push(firstAbbrev + secondWord);
  }

  // Strategy 3: First word only (if not already included)
  if (words.length > 0) {
    const firstWord = words[0].substring(0, 8);
    if (!suggestions.includes(firstWord)) {
      suggestions.push(firstWord);
    }
  }

  // Strategy 4: Concatenate first 2-3 words
  if (words.length >= 2) {
    const combined = words.slice(0, 2).join('').substring(0, 12);
    if (!suggestions.includes(combined)) {
      suggestions.push(combined);
    }
  }

  // Remove duplicates and ensure all are valid
  return [...new Set(suggestions)].filter(handle => handle && handle.length >= 2).slice(0, 5); // Return top 5 suggestions
}

/**
 * Validates a handle according to platform rules
 * @param handle - The handle to validate
 * @returns Object with validation result and error message if invalid
 */
export function validateHandle(handle: string): { isValid: boolean; error?: string } {
  if (!handle || typeof handle !== 'string') {
    return { isValid: false, error: 'Handle is required' };
  }

  const trimmed = handle.trim();

  if (trimmed.length < 2) {
    return { isValid: false, error: 'Handle must be at least 2 characters long' };
  }

  if (trimmed.length > 20) {
    return { isValid: false, error: 'Handle must be 20 characters or less' };
  }

  if (!/^[a-z0-9]+$/.test(trimmed)) {
    return { isValid: false, error: 'Handle can only contain lowercase letters and numbers' };
  }

  if (/^\d+$/.test(trimmed)) {
    return { isValid: false, error: 'Handle cannot be all numbers' };
  }

  // Reserved words
  const reservedWords = [
    'api',
    'www',
    'mail',
    'ftp',
    'admin',
    'administrator',
    'root',
    'system',
    'test',
    'demo',
    'example',
    'null',
    'undefined',
    'support',
    'help',
    'about',
    'contact',
    'privacy',
    'terms',
    'login',
    'signup',
    'auth',
  ];

  if (reservedWords.includes(trimmed.toLowerCase())) {
    return { isValid: false, error: 'This handle is reserved and cannot be used' };
  }

  return { isValid: true };
}

/**
 * Generate SHA-256 hash of file contents for duplicate prevention
 * Uses Web Crypto API for consistent, secure hashing
 * @param file - The file to hash
 * @returns Promise<string> - SHA-256 hash as hex string (64 characters)
 */
export async function generateFileHash(file: File): Promise<string> {
  try {
    // Read file as array buffer
    const arrayBuffer = await file.arrayBuffer();

    // Generate SHA-256 hash
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);

    // Convert to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  } catch (error) {
    console.error('Error generating file hash:', error);
    throw new Error('Failed to generate file hash');
  }
}

/**
 * Check if a resume (by hash) has already been uploaded
 * External flow: Check by position_id (same resume can't be uploaded to same position)
 * Internal flow: Check by company_id (same resume can't be uploaded to same company)
 * @param resumeHash - SHA-256 hash of the resume file
 * @param positionId - The position ID to check against (for external) or null (for internal)
 * @param uploadType - 'external' or 'internal' to determine deduplication logic
 * @param companyId - Required for internal uploads, optional for external (derived from position)
 * @returns Promise<{isDuplicate: boolean, existingUpload?: any}> - Duplicate check result
 */
export async function checkResumeUploadDuplicate(
  resumeHash: string,
  positionId: string | null,
  uploadType: 'external' | 'internal' = 'external',
  companyId?: string
): Promise<{
  isDuplicate: boolean;
  existingUpload?: {
    id: string;
    candidate_id: string;
    created_at: string;
    upload_type: string;
  };
}> {
  try {
    // Include position_id and company_id in SELECT for RLS policy evaluation
    let query = supabase
      .from('resume_uploads')
      .select('id, candidate_id, created_at, upload_type, position_id, company_id')
      .eq('resume_hash', resumeHash)
      .eq('upload_type', uploadType);

    if (uploadType === 'external') {
      // External: Check for same resume + position combination
      if (!positionId) {
        throw new Error('Position ID required for external upload duplicate check');
      }
      query = query.eq('position_id', positionId);
    } else {
      // Internal: Check for same resume + company combination
      if (!companyId) {
        throw new Error('Company ID required for internal upload duplicate check');
      }
      query = query.eq('company_id', companyId);
    }

    const { data, error } = await query.limit(1);

    if (error) {
      console.error('Error checking resume duplicate:', error);
      throw new Error('Failed to check for duplicate resume');
    }

    // Check if any duplicate was found
    if (!data || data.length === 0) {
      return { isDuplicate: false };
    }

    return { isDuplicate: true, existingUpload: data[0] };
  } catch (error) {
    console.error('Error in checkResumeUploadDuplicate:', error);
    throw error;
  }
}

/**
 * Sanitize a file name for safe storage upload
 * Removes/replaces special characters that can cause InvalidKey errors in S3/Supabase Storage
 * @param fileName - Original file name to sanitize
 * @param maxLength - Maximum length for the sanitized name (default: 200)
 * @returns Sanitized file name safe for storage
 */
export function sanitizeFileName(fileName: string, maxLength: number = 200): string {
  if (!fileName || typeof fileName !== 'string') {
    return 'file.pdf';
  }

  // Split into name and extension
  const lastDotIndex = fileName.lastIndexOf('.');
  let name = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  let extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex + 1) : 'pdf';

  // Sanitize the extension (allow only alphanumeric)
  extension = extension.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  if (!extension) {
    extension = 'pdf';
  }

  // Sanitize the name
  name = name
    .trim()
    // Replace apostrophes, quotes, and similar characters with nothing
    .replace(/['''"""]/g, '')
    // Replace spaces and underscores with hyphens
    .replace(/[\s_]+/g, '-')
    // Remove any characters that aren't alphanumeric, hyphens, or dots
    .replace(/[^a-zA-Z0-9.-]/g, '')
    // Replace multiple consecutive hyphens/dots with single ones
    .replace(/[-]+/g, '-')
    .replace(/[.]+/g, '.')
    // Remove leading/trailing hyphens and dots
    .replace(/^[-.]+|[-.]+$/g, '');

  // If name is empty after sanitization, use a default
  if (!name) {
    name = 'resume';
  }

  // Combine and enforce max length
  const sanitized = `${name}.${extension}`;
  if (sanitized.length > maxLength) {
    // Truncate the name part while preserving extension
    const maxNameLength = maxLength - extension.length - 1; // -1 for the dot
    return `${name.substring(0, maxNameLength)}.${extension}`;
  }

  return sanitized;
}

/**
 * Generate a URL-friendly slug for a position
 * Format: {title-truncated-30-chars}-{first-4-chars-of-uuid}
 * Example: "software-engineer-a7f3"
 * @param title - The position title
 * @param positionId - The position's UUID
 * @returns URL-friendly slug
 */
export function generatePositionSlug(title: string, positionId: string): string {
  // Convert to lowercase, replace non-alphanumeric with dashes, collapse multiple dashes
  let baseSlug = title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, ''); // Remove leading/trailing dashes

  // Truncate to 30 chars and remove trailing dash if present
  if (baseSlug.length > 30) {
    baseSlug = baseSlug.substring(0, 30).replace(/-$/, '');
  }

  // Get first 4 chars of UUID (without dashes)
  const shortId = positionId.replace(/-/g, '').substring(0, 4);

  return `${baseSlug}-${shortId}`;
}

// Export types for filter functions
export type CandidateSearchable = {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
};

export type CompanySearchable = {
  name?: string | null;
};

export type PositionSearchable = {
  title?: string | null;
  role_overview?: string | null;
};

export type DisplayPositionSearchable = {
  title?: string | null;
  department?: string | null;
  location?: string | null;
  company?: string | null;
  experienceLevel?: string | null;
  employmentType?: string | null;
};

export type TranscriptEntry = {
  id: string;
  text: string | null;
  session_id: string;
  interview_sessions?: any;
};

/**
 * Filter candidates by search term using token-based matching
 * Matches all tokens against first_name, last_name, and email fields
 * @param candidates - Array of candidate objects to filter
 * @param searchTerm - Search query string (can be multi-word)
 * @returns Filtered array of candidates with the same type as input
 */
export function filterCandidatesBySearch<T extends CandidateSearchable>(
  candidates: T[],
  searchTerm: string
): T[] {
  if (!searchTerm.trim()) return candidates;

  const search = searchTerm.trim().toLowerCase();
  const tokens = search.split(/\s+/).filter(Boolean);

  return candidates.filter((candidate): candidate is T => {
    const first = candidate.first_name?.toLowerCase() || '';
    const last = candidate.last_name?.toLowerCase() || '';
    const email = candidate.email?.toLowerCase() || '';
    const credentials = `${first} ${last} ${email}`;

    return tokens.every(t => credentials.includes(t));
  }) as T[];
}

/**
 * Filter companies by search term using token-based matching
 * Matches all tokens against name field
 * @param companies - Array of company objects to filter
 * @param searchTerm - Search query string (can be multi-word)
 * @returns Filtered array of companies
 */
export function filterCompaniesBySearch<T extends CompanySearchable>(
  companies: T[],
  searchTerm: string
): T[] {
  if (!searchTerm.trim()) return companies;

  const search = searchTerm.trim().toLowerCase();
  const tokens = search.split(/\s+/).filter(Boolean);

  return companies.filter(company => {
    const name = company.name?.toLowerCase() || '';
    return tokens.every(t => name.includes(t));
  });
}

/**
 * Filter positions by search term using token-based matching
 * Matches all tokens against title and role_overview fields combined
 * @param positions - Array of position objects to filter
 * @param searchTerm - Search query string (can be multi-word)
 * @returns Filtered array of positions with the same type as input
 */
export function filterPositionsBySearch<T extends PositionSearchable>(
  positions: T[],
  searchTerm: string
): T[] {
  if (!searchTerm.trim()) return positions;

  const search = searchTerm.trim().toLowerCase();
  const tokens = search.split(/\s+/).filter(Boolean);

  return positions.filter((position): position is T => {
    const title = position.title?.toLowerCase() || '';
    const roleOverview = position.role_overview?.toLowerCase() || '';
    const combined = `${title} ${roleOverview}`;

    return tokens.every(t => combined.includes(t));
  }) as T[];
}

/**
 * Filter display positions by search term using token-based matching
 * Matches all tokens against title, department, location, company, experienceLevel, and employmentType fields combined
 * @param positions - Array of display position objects to filter
 * @param searchTerm - Search query string (can be multi-word)
 * @returns Filtered array of positions
 */
export function filterDisplayPositionsBySearch<T extends DisplayPositionSearchable>(
  positions: T[],
  searchTerm: string
): T[] {
  if (!searchTerm.trim()) return positions;

  const search = searchTerm.trim().toLowerCase();
  const tokens = search.split(/\s+/).filter(Boolean);

  return positions.filter(position => {
    const title = position.title?.toLowerCase() || '';
    const department = position.department?.toLowerCase() || '';
    const location = position.location?.toLowerCase() || '';
    const company = position.company?.toLowerCase() || '';
    const experienceLevel = position.experienceLevel?.toLowerCase() || '';
    const employmentType = position.employmentType?.toLowerCase() || '';
    const combined = `${title} ${department} ${location} ${company} ${experienceLevel} ${employmentType}`;

    return tokens.every(t => combined.includes(t));
  });
}

/**
 * Filter items by text content using token-based matching
 * Matches all tokens against a text field
 * @param items - Array of items with a text field to filter
 * @param searchTerm - Search query string (can be multi-word)
 * @param getText - Function to extract text from each item
 * @returns Filtered array of items with the same type as input
 */
export function filterByText<T>(
  items: T[],
  searchTerm: string,
  getText: (item: T) => string | null | undefined
): T[] {
  if (!searchTerm.trim()) return items;

  const search = searchTerm.trim().toLowerCase();
  const tokens = search.split(/\s+/).filter(Boolean);

  return items.filter((item): item is T => {
    const text = getText(item)?.toLowerCase() || '';
    return tokens.every(t => text.includes(t));
  }) as T[];
}
